// Block J (UI-22, 2026-10-11): the day boards (Today, Tomorrow) and the list router for My tasks, on
// the v2 kit (src/ui) with tasks.css. The board follows the Block I Team board; lists and cards follow
// the kit's list and card patterns. Data, timers and requests are as they were. Fixed with it, each
// failing first in BlockJTasks.test.js:
//   - "Mine" took creator OR userId, so a task assigned to you was not yours and one you handed to a
//     colleague was. It is now the tasks you are responsible for (isTaskResponsibility).
//   - every drag has a tap route: "Move to…" on each card calls the same update as the drop.
import React, { useState, useRef, useEffect, useCallback } from "react";
import { userMeetsPlan } from "./PlanGate";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import useExternalCalendarEvents, { filterEventsForDate, fmtSyncedTime, PROVIDER_LABEL, PROVIDER_GLYPH } from "../hooks/useExternalCalendarEvents";
import AllTasksView from "./AllTasksView";
import { MoveCountsProvider, MovedTag } from "./task/MovedTag";   // 2026-10-21: "Moved 3×"
import { messageFromError } from '../utils/apiError';   // Block 233
import { showErrorToast, showToast } from '../utils/toast';        // Block 233; fix(toast): the shared toast
import { isTaskOverdue, formatTaskClock, formatTaskTimeRange, isTaskResponsibility } from "../utils/taskTime"; // fix(task-dates): the shared formatters; Block D20 Part A: overdue in the user's STORED timezone
import { PRIORITY_RANK } from "../utils/priorityColors";
import { isCalendarEventTask, taskDisplayTitle } from "../utils/calendarTask";   // Fresh-eyes 19
import {
  Banner, Button, Checkbox, ChipGroup, EmptyState as KitEmptyState, ErrorState, Modal, MoveToSheet,
  SegmentedControl, Skeleton, StatusPill, AddTaskButton,
} from "../ui";
import { TasksStandalone } from "./TasksShell";
import "./tasks.css";

const COLS = [
  { id: "todo", label: "To do", tint: "var(--pastel-yellow)", icon: "📋" },
  { id: "inProgress", label: "In progress", tint: "var(--pastel-blue)", icon: "⏳" },
  { id: "done", label: "Completed", tint: "var(--pastel-mint)", icon: "✅" },
];

const VIEW_OPTIONS = [
  { value: "kanban", label: "Board" },
  { value: "list", label: "List" },
  { value: "priority", label: "Priority" },
];

const TIME_PERIODS = [
  { id: "morning", label: "Morning", icon: "🌅", from: 0, to: 12 },
  { id: "afternoon", label: "Afternoon", icon: "☀️", from: 12, to: 17 },
  { id: "evening", label: "Evening", icon: "🌙", from: 17, to: 24 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function getStatus(task) {
  if (task.completed) return "done";
  if (task.inProgress) return "inProgress";
  return "todo";
}

// Block D20 Part A: overdue in the user's STORED timezone (set on render in TaskBoard).
function isOverdue(task) { return isTaskOverdue(task, isOverdue.tz); }

function fmtTime(dt) {
  return formatTaskClock(dt) || null;   // fix(task-dates): the shared formatter
}

function hasScheduledTime(task) {
  if (!task.datetime) return false;
  const d = new Date(task.datetime);
  if (isNaN(d.getTime())) return false;
  // treat midnight (00:00) with no explicit time as "no time"
  return !(d.getHours() === 0 && d.getMinutes() === 0 && !task.time);
}

function sortByPriorityTime(list) {
  return [...list].sort((a, b) => {
    const pa = PRIORITY_RANK[a.priority] ?? 2, pb = PRIORITY_RANK[b.priority] ?? 2;
    if (pa !== pb) return pa - pb;
    return new Date(a.datetime || 0) - new Date(b.datetime || 0);
  });
}
function sortByTime(list) {
  return [...list].sort((a, b) => new Date(a.datetime || 0) - new Date(b.datetime || 0));
}

// Merge timed tasks and timed synced events into per-period item arrays, sorted chronologically
function buildPeriodItems(tasks, syncedEvents) {
  const timedTasks = tasks.filter(hasScheduledTime).map(t => ({
    type: 'task', time: new Date(t.datetime), data: t,
  }));
  const timedEvents = (syncedEvents || []).filter(e => !e.allDay && e.startTime).map(e => ({
    type: 'event', time: new Date(e.startTime), data: e,
  }));
  const allTimed = [...timedTasks, ...timedEvents].sort((a, b) => a.time - b.time);
  return TIME_PERIODS.map(p => ({
    ...p,
    items: allTimed.filter(item => { const h = item.time.getHours(); return h >= p.from && h < p.to; }),
  })).filter(p => p.items.length > 0);
}

function statusToUpdates(statusId) {
  if (statusId === "done") return { completed: true, inProgress: false };
  if (statusId === "inProgress") return { completed: false, inProgress: true };
  return { completed: false, inProgress: false };
}

const priorityKey = (p) => {
  const k = String(p || "medium").toLowerCase();
  return ["low", "medium", "high"].includes(k) ? k : "medium";
};
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// ── Shared sub-components ─────────────────────────────────────────────────────
function PriorityMark({ priority }) {
  return <span className={`tk-dot tk-dot--${priorityKey(priority)}`} aria-hidden="true" />;
}

// Fresh-eyes 19: a calendar event (booking, synced event, accepted invite or suggestion) is a
// meeting: its own mark and a "Meeting" label, never a priority (utils/calendarTask).
function TaskMark({ task }) {
  if (isCalendarEventTask(task)) return <span className="tk-dot tk-dot--meeting" aria-hidden="true" />;
  return <PriorityMark priority={task.priority} />;
}
const isPriorityTask = (t) => !isCalendarEventTask(t);

// The project's own colour is the owner's data, so it is passed as a value, never a token.
function ProjectChip({ name, color }) {
  return (
    <span className="tk-project" title={name}>
      <span className="tk-project__swatch" style={color ? { '--tk-project': color } : undefined} aria-hidden="true" />
      <span className="tk-project__name">{name}</span>
    </span>
  );
}

function OverdueBadge() {
  const { t } = useTranslation('common');
  return <StatusPill tone="warning">{t('tasks.overdue', 'Overdue')}</StatusPill>;
}

function TimerBtn({ taskId, task, activeTimers, onToggle, loggedMinutes }) {
  const running = !!activeTimers[taskId];
  const secs = activeTimers[taskId]?.elapsed || 0;
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  return (
    <span className="tk-timer">
      <Button size="sm" variant={running ? "dark" : "tertiary"} onClick={() => onToggle(taskId, task)}
        aria-label={`${running ? "Stop" : "Start"} the timer for "${task.title}"`}>
        {running ? "⏹ Stop" : "▶ Start"}
      </Button>
      {secs > 0 && <span className="tk-timer__time">{mm}:{ss}</span>}
      {!running && loggedMinutes > 0 && (
        <span className="tk-timer__time">
          {Math.floor(loggedMinutes / 60) > 0 ? `${Math.floor(loggedMinutes / 60)}h ` : ""}{loggedMinutes % 60 > 0 ? `${loggedMinutes % 60}m` : ""} logged
        </span>
      )}
    </span>
  );
}

function StatusCycleBtn({ task, onMove }) {
  const { t } = useTranslation('common');
  const status = getStatus(task);
  const next = status === "todo" ? "inProgress" : status === "inProgress" ? "done" : "todo";
  const labels = {
    todo: t('tasks.status.todo'),
    inProgress: t('tasks.status.in_progress'),
    done: t('tasks.status.done'),
  };
  return (
    <Button size="sm" variant="tertiary" onClick={() => onMove(task.id, statusToUpdates(next))} title={`Move to ${labels[next]}`}
      aria-label={`${labels[status]}: move "${task.title}" to ${labels[next]}`}>
      {labels[status]}
    </Button>
  );
}

// ── View toggle ───────────────────────────────────────────────────────────────
function ViewToggle({ view, onChange }) {
  return <SegmentedControl label="View" options={VIEW_OPTIONS} value={view} onChange={onChange} />;
}

// ── Filter chips ──────────────────────────────────────────────────────────────
const FILTERS = [
  { id: "all", label: "All" },
  { id: "mine", label: "Mine" },
  { id: "overdue", label: "Overdue" },
  { id: "high", label: "High priority" },
];

function FilterPills({ active, onChange, counts }) {
  return (
    <ChipGroup label="Show" value={active} onChange={onChange}
      options={FILTERS.map(f => ({ value: f.id, label: counts?.[f.id] > 0 ? `${f.label} (${counts[f.id]})` : f.label }))} />
  );
}

// ── Kanban card ───────────────────────────────────────────────────────────────
function KanbanCard({ task, onView, onMoveTo, isPro, activeTimers, onTimerToggle, taskTypes, loggedMinutes }) {
  const overdue = isOverdue(task);
  const time = fmtTime(task.datetime);
  const status = getStatus(task);
  const typeLabel = task.taskType && taskTypes ? (taskTypes.find(t => t.value === task.taskType)?.label || task.taskType) : null;
  return (
    <article
      className={`tk-card${status === "done" ? " tk-card--done" : ""}`}
      draggable
      onDragStart={e => e.dataTransfer.setData("taskId", String(task.id))}
    >
      <button type="button" className="tk-card__open" onClick={() => onView(task)}>
        <TaskMark task={task} />
        <span className="tk-card__title">{taskDisplayTitle(task)}</span>
      </button>
      <div className="tk-card__meta">
        {isCalendarEventTask(task) ? <StatusPill tone="neutral">Meeting</StatusPill> : <StatusPill priority={priorityKey(task.priority)} />}
        {task.projectName && <ProjectChip name={task.projectName} color={task.projectColor} />}
        {time && <StatusPill tone="neutral">{time}</StatusPill>}
        {overdue && <OverdueBadge />}
        <MovedTag task={task} />
        {typeLabel && !isCalendarEventTask(task) && <span className="tk-small">{typeLabel}</span>}
      </div>
      <div className="tk-card__foot">
        {isPro && status !== "done" ? <TimerBtn taskId={task.id} task={task} activeTimers={activeTimers} onToggle={onTimerToggle} loggedMinutes={loggedMinutes?.[String(task.id)] || 0} /> : <span />}
        <Button size="sm" variant="tertiary" aria-label={`Move "${task.title}" to…`} onClick={() => onMoveTo(task)}>Move to…</Button>
      </div>
    </article>
  );
}

// ── Kanban column ─────────────────────────────────────────────────────────────
function KanbanCol({ col, tasks, onView, onDrop, onAddTask, onMoveTo, isPro, activeTimers, onTimerToggle, taskTypes, loggedMinutes }) {
  const [over, setOver] = useState(false);
  return (
    <section
      className={`tk-col${over ? " tk-col--over" : ""}`}
      aria-label={col.label}
      style={{ '--tk-col': col.tint }}
      onDragOver={e => { e.preventDefault(); if (!over) setOver(true); }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
      onDrop={e => { e.preventDefault(); setOver(false); onDrop(e, col.id); }}
    >
      <div className="tk-col__head">
        <h3 className="tk-col__title">{col.label}</h3>
        <StatusPill tone="neutral">{tasks.length}</StatusPill>
      </div>
      <div className="tk-col__body">
        {tasks.length === 0 ? (
          <div className="tk-col__empty">
            <span aria-hidden="true">{col.icon}</span>
            <span>{col.id === "done" ? "Nothing completed yet" : `Nothing ${col.id === "inProgress" ? "in progress" : "to do"} yet`}</span>
            {col.id !== "done" && <AddTaskButton size="sm" onClick={onAddTask} />}
          </div>
        ) : tasks.map(task => (
          <KanbanCard key={task.id} task={task} onView={onView} onMoveTo={onMoveTo} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
        ))}
      </div>
    </section>
  );
}

// ── Task row (list and priority views) ────────────────────────────────────────
function TaskRow({ task, onView, onMove, isPro, activeTimers, onTimerToggle, loggedMinutes }) {
  const overdue = isOverdue(task);
  const time = fmtTime(task.datetime);
  return (
    <div className={`tk-row${task.completed ? " tk-row--done" : ""}`}>
      <span className="tk-row__dot"><TaskMark task={task} /></span>
      <button type="button" className="tk-row__main" onClick={() => onView(task)}>
        <span className="tk-row__title">{taskDisplayTitle(task)}</span>
        {(time || overdue || task.projectName || isCalendarEventTask(task)) && (
          <span className="tk-row__meta">
            {isCalendarEventTask(task) && <StatusPill tone="neutral">Meeting</StatusPill>}
            {time && <span>{time}</span>}
            {overdue && <OverdueBadge />}
            <MovedTag task={task} />
            {task.projectName && <ProjectChip name={task.projectName} color={task.projectColor} />}
          </span>
        )}
      </button>
      <div className="tk-row__actions">
        {isPro && !task.completed && <TimerBtn taskId={task.id} task={task} activeTimers={activeTimers} onToggle={onTimerToggle} loggedMinutes={loggedMinutes?.[String(task.id)] || 0} />}
        {!task.completed && (
          <Button size="sm" variant="confirm" onClick={() => onMove(task.id, { completed: true, inProgress: false })} aria-label={`Mark "${task.title}" done`}>✓ Done</Button>
        )}
        <StatusCycleBtn task={task} onMove={onMove} />
      </div>
    </div>
  );
}

// A titled group of rows that folds.
function Group({ title, sub, tint, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`tk-group${open ? "" : " tk-group--closed"}`} style={tint ? { '--tk-group': tint } : undefined}>
      <button type="button" className="tk-group__head" aria-expanded={open} onClick={() => setOpen(v => !v)}>
        <span className="tk-group__title">{title}</span>
        {sub && <span className="tk-group__sub">{sub}</span>}
        <span className="tk-group__chev" aria-hidden="true">▾</span>
      </button>
      {open && <div className="tk-group__body">{children}</div>}
    </section>
  );
}

const PRIORITY_TINT = { High: "var(--priority-high)", Medium: "var(--priority-medium)", Low: "var(--priority-low)" };

// ── Priority section (collapsible) ────────────────────────────────────────────
function PrioritySection({ priority, tasks, onView, onMove, isPro, activeTimers, onTimerToggle, taskTypes, onAddTask, dayLabel, loggedMinutes }) {
  return (
    <Group title={`${priority} priority`} sub={plural(tasks.length, "task")} tint={PRIORITY_TINT[priority]}>
      {tasks.length === 0 ? (
        <p className="tk-group__empty">
          No {priority.toLowerCase()} priority tasks {dayLabel}.{" "}
          <Button size="sm" variant="tertiary" onClick={onAddTask}>+ Add one</Button>
        </p>
      ) : tasks.map(task => (
        <TaskRow key={task.id} task={task} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
      ))}
    </Group>
  );
}

// ── Synced event row (read-only, visually distinct from task rows) ────────────
function SyncedEventRow({ event }) {
  const glyph = PROVIDER_GLYPH[event.provider] || '📅';
  const providerLabel = PROVIDER_LABEL[event.provider] || 'Calendar';
  const timeStr = event.startTime ? fmtSyncedTime(event.startTime) : null;
  return (
    <div className="tk-event" title={`${event.title || 'Event'}${timeStr ? '\n' + timeStr : ''}\nSynced from ${providerLabel}`}>
      <span aria-hidden="true">{glyph}</span>
      <span className="tk-bar__title">
        <span className="tk-event__title">{event.title || 'Untitled event'}</span>
        <span className="tk-small">{[timeStr, `from ${providerLabel}`].filter(Boolean).join(' · ')}</span>
      </span>
    </div>
  );
}

// ── All-day event banner (pinned above timed periods) ─────────────────────────
function AllDayBanner({ events }) {
  if (!events.length) return null;
  return (
    <div className="tk-rows">
      {events.map((ev, i) => (
        <div key={ev.id || i} className="tk-event">
          <span aria-hidden="true">{PROVIDER_GLYPH[ev.provider] || '📅'}</span>
          <span className="tk-event__title">All day: {ev.title || 'Untitled event'}</span>
        </div>
      ))}
    </div>
  );
}

// ── Shared period section renderer (tasks + events interleaved) ───────────────
function PeriodSection({ period, onView, onMove, isPro, activeTimers, onTimerToggle, taskTypes, loggedMinutes }) {
  return (
    <section className="tk-period">
      <h3 className="tk-period__head">
        <span aria-hidden="true">{period.icon}</span>
        {period.label}
        <span className="tk-small">{period.from === 0 ? "before noon" : period.from === 12 ? "noon to 5 pm" : "after 5 pm"}</span>
      </h3>
      {period.items.map(item =>
        item.type === 'task'
          ? <TaskRow key={`t-${item.data.id}`} task={item.data} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
          : <SyncedEventRow key={`e-${item.data.id || item.data.externalId}`} event={item.data} />
      )}
    </section>
  );
}

// ── Today list view ───────────────────────────────────────────────────────────
function TodayListView({ filtered, syncedEvents, onView, onMove, isPro, activeTimers, onTimerToggle, taskTypes, onAddTask, loggedMinutes, tasksLoadFailed = false }) {
  const unscheduled = sortByPriorityTime(filtered.filter(t => !hasScheduledTime(t)));
  const allDayEvents = (syncedEvents || []).filter(e => e.allDay);
  const byPeriod = buildPeriodItems(filtered, syncedEvents);

  if (filtered.length === 0 && (!syncedEvents || syncedEvents.length === 0)) return <EmptyState onAddTask={onAddTask} failed={tasksLoadFailed} />;

  return (
    <div className="tk-stack">
      <AllDayBanner events={allDayEvents} />
      {unscheduled.length > 0 && (
        <Group title="📌 Unscheduled" sub={plural(unscheduled.length, "task")}>
          {unscheduled.map(task => (
            <TaskRow key={task.id} task={task} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
          ))}
        </Group>
      )}
      {byPeriod.map(period => (
        <PeriodSection key={period.id} period={period} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
      ))}
    </div>
  );
}

// ── Tomorrow list view (special) ──────────────────────────────────────────────
function TomorrowListView({ filtered, syncedEvents, onView, onMove, isPro, activeTimers, onTimerToggle, taskTypes, onAddTask, loggedMinutes, tasksLoadFailed = false }) {
  const unscheduled = sortByPriorityTime(filtered.filter(t => !hasScheduledTime(t)));
  const allDayEvents = (syncedEvents || []).filter(e => e.allDay);
  const byPeriod = buildPeriodItems(filtered, syncedEvents);

  if (filtered.length === 0 && (!syncedEvents || syncedEvents.length === 0)) return <EmptyState onAddTask={onAddTask} failed={tasksLoadFailed} />;

  const scheduledTaskCount = filtered.filter(hasScheduledTime).length;
  const timedEventCount = (syncedEvents || []).filter(e => !e.allDay && e.startTime).length;
  const timedTotal = scheduledTaskCount + timedEventCount;

  return (
    <div className="tk-stack">
      <AllDayBanner events={allDayEvents} />

      <Group title="📌 Unscheduled" sub={plural(unscheduled.length, "task")}>
        {unscheduled.length === 0 ? (
          <p className="tk-group__empty">
            No unscheduled tasks.{" "}
            <Button size="sm" variant="tertiary" onClick={onAddTask}>+ Add one</Button>
          </p>
        ) : unscheduled.map(task => (
          <TaskRow key={task.id} task={task} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
        ))}
      </Group>

      {/* Scheduled tasks + synced events interleaved by time period */}
      {timedTotal > 0 && (
        <div className="tk-stack">
          <div className="tk-divider">
            Scheduled · {plural(scheduledTaskCount, "task")}{timedEventCount > 0 ? ` and ${plural(timedEventCount, "event")}` : ""}
          </div>
          {byPeriod.map(period => (
            <PeriodSection key={period.id} period={period} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
          ))}
        </div>
      )}

      {timedTotal === 0 && unscheduled.length > 0 && (
        <p className="tk-muted">
          No timed tasks yet.{" "}
          <Button size="sm" variant="tertiary" onClick={onAddTask}>+ Schedule one</Button>
        </p>
      )}
    </div>
  );
}

// ── AI plan panel (modal) ─────────────────────────────────────────────────────
function AiPlanPanel({ tasks, date, dateLabel, onClose, onAccept }) {
  const _aiDayStart = new Date(date); _aiDayStart.setHours(0, 0, 0, 0);
  const _aiDayEnd   = new Date(date); _aiDayEnd.setHours(23, 59, 59, 999);
  const { events: _syncedAiEvents } = useExternalCalendarEvents(_aiDayStart.toISOString(), _aiDayEnd.toISOString());

  const [aiLoading, setAiLoading] = useState(true);
  const [suggestions, setSuggestions] = useState(null);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState({});
  const [accepted, setAccepted] = useState(false);
  const hasRun = useRef(false);
  const eventsRef = useRef([]);

  const tomorrowStr = date.toDateString();

  // Keep a ref to the latest filtered calendar events
  useEffect(() => {
    eventsRef.current = _syncedAiEvents
      .map(e => ({
        title: e.title || 'Untitled',
        start: e.startTime,
        end: e.endTime || null,
      }))
      .filter(e => {
        if (!e.start) return false;
        try { return new Date(e.start).toDateString() === tomorrowStr; } catch { return false; }
      });
  }, [_syncedAiEvents, tomorrowStr]);

  // Wait for calendar events to finish loading (up to 2 s), then call AI
  useEffect(() => {
    if (hasRun.current) return;
    const delay = _aiDayStart ? 500 : 200;
    const timer = setTimeout(() => {
      if (hasRun.current) return;
      hasRun.current = true;

      const calMeetings = eventsRef.current.map(e => ({
        title: e.title,
        start: fmtTime(e.start),
        end: fmtTime(e.end),
      }));

      const payload = {
        date: dateLabel,
        calendarMeetings: calMeetings,
        tasks: tasks.map(t => ({
          id: t.id,
          title: t.title,
          priority: t.priority || "Medium",
          currentTime: fmtTime(t.datetime) || null,
          project: t.projectName || null,
          estimatedMinutes: t.estimatedDuration || null,
        })),
      };

      api('/api/aidey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: `You are Aidey, a sharp executive assistant. Analyse the user's tasks and calendar meetings for ${dateLabel}.

Steps:
1. Identify fixed calendar meetings that cannot move.
2. Calculate available time gaps between meetings and at the start/end of the day.
3. Suggest an optimal time slot for each task based on: task priority, estimated duration (default 30 min if unknown), available gaps, and focus type (e.g. deep work needs an uninterrupted block).

Return ONLY valid JSON, no markdown fences, no prose outside the JSON:
{
  "dayOverview": "1-2 sentence summary of how tomorrow looks",
  "suggestions": [
    {
      "taskId": <number or null if unknown>,
      "taskTitle": "exact task title from input",
      "suggestedTime": "9:00 AM",
      "reason": "plain English explanation, e.g. 'Scheduled before your 10am call as it only needs 20 minutes'"
    }
  ]
}

Only include tasks you are actually repositioning or explaining. Omit tasks that are already well placed. Return valid JSON only.`,
          messages: [{ role: 'user', content: JSON.stringify(payload) }],
        }),
      })
        .then(r => r.json())
        .then(d => {
          const raw = (d.reply || '').trim();
          const match = raw.match(/\{[\s\S]*\}/);
          if (!match) throw new Error('no JSON');
          const parsed = JSON.parse(match[0]);
          const suggs = Array.isArray(parsed.suggestions) ? parsed.suggestions : [];
          setOverview(parsed.dayOverview || null);
          setSuggestions(suggs);
          const init = {};
          suggs.forEach(s => { if (s.taskId != null) init[s.taskId] = true; });
          setChecked(init);
          setAiLoading(false);
        })
        .catch(() => {
          setError("Couldn't generate a plan. Check your connection and try again.");
          setAiLoading(false);
        });
    }, delay);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function applySelected() {
    if (!suggestions) return;
    suggestions.forEach(s => {
      if (s.taskId != null && checked[s.taskId] && s.suggestedTime) {
        const parts = s.suggestedTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (parts) {
          let h = parseInt(parts[1]);
          const m = parseInt(parts[2]);
          const ampm = parts[3].toUpperCase();
          if (ampm === 'PM' && h !== 12) h += 12;
          if (ampm === 'AM' && h === 12) h = 0;
          const dt = new Date(date);
          dt.setHours(h, m, 0, 0);
          onAccept(s.taskId, { datetime: dt.toISOString() });
        }
      }
    });
    setAccepted(true);
    setTimeout(onClose, 900);
  }

  const checkedCount = Object.values(checked).filter(Boolean).length;

  // Render current calendar events for display in the modal
  const displayEvents = eventsRef.current;

  return (
    <Modal open onClose={onClose} title={`Aidey's plan for ${dateLabel}`} color="pastel-periwinkle" width={560}
      footer={suggestions && !aiLoading && !accepted ? (
        <>
          <span className="tk-small">{plural(checkedCount, "suggestion")} selected</span>
          <Button variant="tertiary" onClick={onClose}>Dismiss</Button>
          {checkedCount > 0 && (
            <Button variant="confirm" onClick={applySelected}>Apply {checkedCount > 1 ? `${checkedCount} changes` : "change"}</Button>
          )}
        </>
      ) : null}>
      <div className="tk-stack">
        <p className="tk-muted">Choose the suggestions to apply, then confirm. Nothing moves until you approve it.</p>

        {aiLoading && <Skeleton lines={3} label="Analysing your tasks and calendar" />}

        {error && <Banner tone="problem">{error}</Banner>}

        {accepted && <Banner tone="success">Applied. Your tasks have been rescheduled.</Banner>}

        {suggestions && !aiLoading && !accepted && (
          <>
            {overview && <p className="tk-muted">{overview}</p>}

            {displayEvents.length > 0 && (
              <section className="tk-period">
                <h3 className="tk-period__head">Fixed meetings</h3>
                {displayEvents.slice(0, 6).map((e, i) => (
                  <div key={i} className="tk-event">
                    <span aria-hidden="true">📌</span>
                    <span className="tk-event__title">{e.title}</span>
                    {e.start && <span className="tk-small">{fmtTime(e.start)}{e.end && ` to ${fmtTime(e.end)}`}</span>}
                  </div>
                ))}
              </section>
            )}

            <section className="tk-period">
              <h3 className="tk-period__head">Suggestions ({suggestions.length})</h3>
              {suggestions.length === 0 ? (
                <p className="tk-muted">Your tasks look well arranged. No changes suggested.</p>
              ) : suggestions.map((s, i) => (
                <div key={i} className="tk-row" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                  {s.taskId != null ? (
                    <Checkbox checked={!!checked[s.taskId]} onChange={(on) => setChecked(prev => ({ ...prev, [s.taskId]: on }))}
                      label={<span className="tk-bar__title"><strong>{s.taskTitle}</strong>{s.suggestedTime && <span className="tk-small">{s.suggestedTime}</span>}<span className="tk-small">{s.reason}</span></span>} />
                  ) : (
                    <span className="tk-bar__title"><strong>{s.taskTitle}</strong>{s.suggestedTime && <span className="tk-small">{s.suggestedTime}</span>}<span className="tk-small">{s.reason}</span></span>
                  )}
                </div>
              ))}
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
//
// "WIFI OFF, OPENED A LIST SCREEN: IT SHOWED AS 'no tasks'." (device sitting, 2026-10-04)
//
// An empty list and a failed load look identical and mean opposite things. App.js has
// ALWAYS known the difference — it sets `tasksLoadFailed` on a failed fetch, with the
// comment "a failed load must not read as all clear" — but it passed the flag to the
// DASHBOARD only. Every task LIST screen rendered this tick-and-"No tasks" with no idea a
// request had failed. A fix that reached one consumer out of several: the second-path rule
// again, and this time the second path was a prop that was never threaded.
//
// A tick and "No tasks match this filter" is the reassurance shape at its plainest: it
// tells her the work is done when the truth is that nothing could be read.
// Block J: "Try again" asks App to reload the tasks (the aidey:tasksUpdated refetch).
const reloadTasks = () => window.dispatchEvent(new Event('aidey:tasksUpdated'));

function EmptyState({ onAddTask, failed = false }) {
  if (failed) {
    return (
      <div data-testid="tasks-load-failed">
        <ErrorState title="Your tasks could not be loaded."
          message="This is not an empty list, we could not reach your tasks, so nothing here is a picture of your day. Check your connection and try again."
          onRetry={reloadTasks} />
      </div>
    );
  }
  return (
    <KitEmptyState icon="✅" title="No tasks match this filter."
      action={<AddTaskButton onClick={onAddTask}>+ Add a task</AddTaskButton>} />
  );
}

// ── Generic task board (Today + Tomorrow) ─────────────────────────────────────
function TaskBoard({ tasks, date, heading, isTomorrow, onViewTask, onMoveTask, onAddTask, currentUser, taskTypes, tasksLoadFailed = false, embedded = false }) {
  isOverdue.tz = currentUser?.timezone; // Block D20 Part A: stored timezone for the overdue helper
  const userId = currentUser?.id;
  const isPro = userMeetsPlan(currentUser, 'pro'); // FIX-3: shared, case-insensitive, trial-aware
  const viewKey = `taskView_${isTomorrow ? "tomorrow" : "today"}_${userId || "default"}`;

  const [view, setView] = useState(() => {
    const isPhone = typeof window !== "undefined" && window.matchMedia('(max-width: 767px)').matches;
    const stored = localStorage.getItem(viewKey);
    if (isPhone && stored === "kanban") return "list";
    if (stored) return stored;
    return isPhone ? "list" : "kanban";
  });
  const [activeFilter, setActiveFilter] = useState("all");
  const [moving, setMoving] = useState(null);   // the task whose "Move to…" sheet is open
  const [activeTimers, setActiveTimers] = useState({});
  const [loggedMinutes, setLoggedMinutes] = useState({});
  const [showAiPlan, setShowAiPlan] = useState(false);
  const timerRefs = useRef({});


  function changeView(v) { setView(v); localStorage.setItem(viewKey, v); }

  // Fetch logged time totals for visible tasks
  const loadLoggedMinutes = useCallback(async (taskList) => {
    if (!isPro || !taskList.length) return;
    const ids = taskList.map(t => String(t.id)).join(',');
    try {
      const res = await api(`/api/time-entries?taskIds=${ids}`);
      const data = await res.json();
      const map = {};
      (data.entries || []).forEach(e => {
        const key = String(e.taskId);
        map[key] = (map[key] || 0) + e.duration;
      });
      setLoggedMinutes(map);
    } catch (_) { }
  }, [isPro]);

  const dateStr = date.toDateString();
  const dateTasks = tasks.filter(t => {
    const ref = t.datetime || (t.date && new Date(t.date + "T00:00:00"));
    return ref && new Date(ref).toDateString() === dateStr;
  });

  // Fetch synced external events for this board's date
  const boardDateStart = new Date(date); boardDateStart.setHours(0, 0, 0, 0);
  const boardDateEnd   = new Date(date); boardDateEnd.setHours(23, 59, 59, 999);
  const localDateStr   = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const { events: allSyncedEvents } = useExternalCalendarEvents(boardDateStart.toISOString(), boardDateEnd.toISOString());
  const syncedEventsForDay = filterEventsForDate(allSyncedEvents, localDateStr);

  useEffect(() => { loadLoggedMinutes(dateTasks); }, [dateTasks.length]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveTimeEntry(taskId, task, elapsed) {
    const durationMins = Math.round(elapsed / 60);
    if (durationMins < 1) return;
    const startDt = new Date(Date.now() - elapsed * 1000);
    try {
      await api('/api/time-entries', {
        method: 'POST',
        body: JSON.stringify({
          taskId: String(taskId),
          projectId: task?.projectId ?? null,
          startTime: startDt.toISOString(),
          endTime: new Date().toISOString(),
          duration: durationMins,
          note: null,
        }),
      });
      // Update local logged total
      setLoggedMinutes(prev => ({ ...prev, [String(taskId)]: (prev[String(taskId)] || 0) + durationMins }));
    } catch (err) { showErrorToast(messageFromError(err, "Could not save time entry.")); }
  }

  function toggleTimer(taskId, task) {
    setActiveTimers(prev => {
      if (prev[taskId]) {
        // Stopping — save the entry
        clearInterval(timerRefs.current[taskId]);
        delete timerRefs.current[taskId];
        const elapsed = prev[taskId].elapsed;
        saveTimeEntry(taskId, task, elapsed);
        const next = { ...prev }; delete next[taskId]; return next;
      }
      const start = Date.now();
      timerRefs.current[taskId] = setInterval(() => {
        setActiveTimers(p => p[taskId] ? { ...p, [taskId]: { ...p[taskId], elapsed: Math.floor((Date.now() - start) / 1000) } } : p);
      }, 1000);
      return { ...prev, [taskId]: { start, elapsed: 0 } };
    });
  }

  useEffect(() => () => Object.values(timerRefs.current).forEach(clearInterval), []);

  // "Mine" is the tasks you are responsible for: the assignees if any, otherwise the creator
  // (utils/taskTime isTaskResponsibility, the rule the server's lib/myTasks uses).
  const isMine = (t) => isTaskResponsibility(t, userId);
  const pillCounts = {
    all: dateTasks.length,
    mine: dateTasks.filter(isMine).length,
    overdue: dateTasks.filter(isOverdue).length,
    high: dateTasks.filter(t => isPriorityTask(t) && t.priority === "High").length,
  };

  const filtered = dateTasks.filter(t => {
    if (activeFilter === "mine") return isMine(t);
    if (activeFilter === "overdue") return isOverdue(t);
    if (activeFilter === "high") return isPriorityTask(t) && t.priority === "High";
    return true;
  });

  const todo = sortByTime(filtered.filter(t => getStatus(t) === "todo"));
  const inProgress = sortByTime(filtered.filter(t => getStatus(t) === "inProgress"));
  const done = filtered.filter(t => getStatus(t) === "done");

  // The drop and "Move to…" are one move: the same update, through the same onMoveTask, with the
  // task's own id (the drag carries it as text).
  function moveTo(task, colId) {
    if (!task || getStatus(task) === colId) return;
    onMoveTask(task.id, statusToUpdates(colId));
  }

  function handleDrop(e, colId) {
    const taskId = e.dataTransfer.getData("taskId");
    if (!taskId) return;
    moveTo(dateTasks.find(t => String(t.id) === String(taskId)), colId);
  }

  // Wrap onViewTask to show client toast for untracked project tasks
  function handleViewTask(task) {
    if (isPro && task?.projectId && !activeTimers[task.id]) {
      // fix(toast): the shared toast (it was its own white box, bottom right).
      showToast(`This is client work. Don't forget to start your timer for ${task.title || 'it'}.`, { type: 'info' });
    }
    onViewTask(task);
  }

  const dateLabel = date.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  const dayLabel = isTomorrow ? "tomorrow" : "today";
  const columns = isTomorrow ? COLS.filter(c => c.id === "todo") : COLS;

  return (
    <TasksStandalone embedded={embedded} title={heading} subline={`${dateLabel} · ${plural(filtered.length, "task")}`} onAddTask={onAddTask}>
      <div className="tk-bar">
        {embedded ? (
          <div className="tk-bar__title">
            <h2>{dateLabel}</h2>
            <p className="tk-muted">{plural(filtered.length, "task")}</p>
          </div>
        ) : <span />}
        <div className="tk-bar__end">
          {isTomorrow && (
            <Button variant="secondary" onClick={() => setShowAiPlan(v => !v)}>🤖 Plan my day with Aidey</Button>
          )}
          <ViewToggle view={view} onChange={changeView} />
        </div>
      </div>

      <FilterPills active={activeFilter} onChange={setActiveFilter} counts={pillCounts} />

      {/* ── AI plan modal ── */}
      {showAiPlan && isTomorrow && (
        <AiPlanPanel
          tasks={dateTasks}
          date={date}
          dateLabel={dateLabel}
          onClose={() => setShowAiPlan(false)}
          onAccept={onMoveTask}
        />
      )}

      {/* ABOVE EVERY VIEW, INCLUDING KANBAN — WHICH IS THE DESKTOP DEFAULT.
          EmptyState only renders in the list and priority views, so on a failed load the
          kanban board showed empty COLUMNS and said nothing at all. She was on a phone,
          where the default is list, which is why she saw "No tasks" rather than silence —
          but silence on the desktop default is the same defect with less to read. A banner
          rather than a replacement, because the columns are the layout and a board with a
          warning over it is honest; a board replaced by a warning loses the drop targets. */}
      {tasksLoadFailed && (
        <div data-testid="tasks-load-failed-banner">
          <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={reloadTasks}>Try again</Button>}>
            Your tasks could not be loaded, this is not an empty board. Check your connection and try again.
          </Banner>
        </div>
      )}

      {/* KANBAN */}
      {view === "kanban" && (
        <div className="tk-board" style={{ '--tk-cols': columns.length }}>
          {columns.map(col => {
            const colTasks = col.id === "todo" ? todo : col.id === "inProgress" ? inProgress : done;
            return (
              <KanbanCol key={col.id} col={col} tasks={colTasks} onView={handleViewTask} onDrop={handleDrop} onAddTask={onAddTask} onMoveTo={setMoving} isPro={isPro} activeTimers={activeTimers} onTimerToggle={toggleTimer} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
            );
          })}
        </div>
      )}

      {/* LIST */}
      {view === "list" && (
        isTomorrow ? (
          <TomorrowListView
            filtered={filtered}
            syncedEvents={syncedEventsForDay}
            onView={handleViewTask}
            onMove={onMoveTask}
            isPro={isPro}
            activeTimers={activeTimers}
            onTimerToggle={toggleTimer}
            taskTypes={taskTypes}
            onAddTask={onAddTask}
            loggedMinutes={loggedMinutes}
            tasksLoadFailed={tasksLoadFailed}
          />
        ) : (
          <TodayListView
            filtered={filtered}
            syncedEvents={syncedEventsForDay}
            onView={handleViewTask}
            onMove={onMoveTask}
            isPro={isPro}
            activeTimers={activeTimers}
            onTimerToggle={toggleTimer}
            taskTypes={taskTypes}
            onAddTask={onAddTask}
            loggedMinutes={loggedMinutes}
            tasksLoadFailed={tasksLoadFailed}
          />
        )
      )}

      {/* PRIORITY */}
      {view === "priority" && (
        filtered.length === 0 ? <EmptyState onAddTask={onAddTask} failed={tasksLoadFailed} /> : (
          <div className="tk-stack">
            {["High", "Medium", "Low"].map(p => {
              const group = filtered.filter(t => isPriorityTask(t) && (t.priority || "Medium") === p);
              if (group.length === 0 && p !== "Medium") return null;
              return (
                <PrioritySection key={p} priority={p} tasks={sortByTime(group)} onView={handleViewTask} onMove={onMoveTask} isPro={isPro} activeTimers={activeTimers} onTimerToggle={toggleTimer} taskTypes={taskTypes} onAddTask={onAddTask} dayLabel={dayLabel} loggedMinutes={loggedMinutes} />
              );
            })}
            {/* Fresh-eyes 19: meetings have no priority, so they are a group of their own. */}
            {filtered.some(isCalendarEventTask) && (
              <Group title="Meetings" sub={plural(filtered.filter(isCalendarEventTask).length, "meeting")} tint="var(--pastel-blue)">
                {sortByTime(filtered.filter(isCalendarEventTask)).map(task => (
                  <TaskRow key={task.id} task={task} onView={handleViewTask} onMove={onMoveTask} isPro={isPro} activeTimers={activeTimers} onTimerToggle={toggleTimer} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
                ))}
              </Group>
            )}
          </div>
        )
      )}

      {/* The tap route for every drag on the board (addendum 7). */}
      <MoveToSheet
        open={!!moving}
        onClose={() => setMoving(null)}
        itemName={moving?.title}
        current={moving ? getStatus(moving) : undefined}
        destinations={COLS.map(c => ({ id: c.id, label: c.label, icon: c.icon, color: c.id === "todo" ? "pastel-yellow" : c.id === "inProgress" ? "pastel-blue" : "pastel-mint" }))}
        onMove={(d) => moveTo(moving, d.id)}
      />
    </TasksStandalone>
  );
}

// ── Main TaskList export ──────────────────────────────────────────────────────
function TaskList({ tasks, mode, sortBy, setSortBy, onViewTask, today, tomorrow, taskTypes, undoComplete, onMoveTask, onAddTask, currentUser, tasksLoadFailed = false, embedded = false }) {

  const tasksForDate = date => tasks.filter(t => new Date(t.datetime).toDateString() === date.toDateString());
  const sortTasks = list => {
    if (sortBy === "time") return [...list].sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
    return [...list].sort((a, b) => {
      const pa = PRIORITY_RANK[a.priority] ?? 2, pb = PRIORITY_RANK[b.priority] ?? 2;
      if (pa !== pb) return pa - pb;
      return new Date(a.datetime) - new Date(b.datetime);
    });
  };

  if (mode === "Today") {
    return <MoveCountsProvider tasks={tasks}><TaskBoard tasks={tasks} date={today} heading="Today's tasks" isTomorrow={false} onViewTask={onViewTask} onMoveTask={onMoveTask || (() => { })} onAddTask={onAddTask || (() => { })} currentUser={currentUser} taskTypes={taskTypes} tasksLoadFailed={tasksLoadFailed} embedded={embedded} /></MoveCountsProvider>;
  }

  if (mode === "Tomorrow") {
    return <MoveCountsProvider tasks={tasks}><TaskBoard tasks={tasks} date={tomorrow} heading="Tomorrow's tasks" isTomorrow={true} onViewTask={onViewTask} onMoveTask={onMoveTask || (() => { })} onAddTask={onAddTask || (() => { })} currentUser={currentUser} taskTypes={taskTypes} tasksLoadFailed={tasksLoadFailed} embedded={embedded} /></MoveCountsProvider>;
  }

  // ── WEEK VIEW ──
  if (mode === "Week View") {
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay() + 1);
    const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
    return (
      <div className="tk-week">
        {days.map((date, idx) => {
          const dayTasks = sortTasks(tasksForDate(date));
          return (
            <section key={idx} className="tk-day">
              <h4 className="tk-day__head">{date.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</h4>
              <div className="tk-day__body">
                {dayTasks.length === 0 ? <p className="tk-day__empty">No tasks</p> :
                  dayTasks.map(task => (
                    <button key={task.id} type="button" className="tk-day__open" onClick={() => onViewTask(task)}>
                      <span className="tk-day__title">{taskDisplayTitle(task)}</span>
                      {task.taskType && <span className="tk-small">{taskTypes?.find(t => t.value === task.taskType)?.label}</span>}
                      <span className="tk-small">{formatTaskTimeRange(task) || "No time"}</span>
                    </button>
                  ))
                }
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  // ── LIST VIEW (All Tasks) ──
  if (mode === "List") {
    return (
      <MoveCountsProvider tasks={tasks}>
      <AllTasksView
        tasks={tasks}
        tasksLoadFailed={tasksLoadFailed}
        embedded={embedded}
        onViewTask={onViewTask}
        onMoveTask={onMoveTask || (() => { })}
        onAddTask={onAddTask || (() => { })}
        currentUser={currentUser}
        taskTypes={taskTypes}
      />
      </MoveCountsProvider>
    );
  }

  return null;
}

export default TaskList;
